import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { EscalationResolve, LoneWorkEscalation, LoneWorkSession } from '@cloudsign/shared';
import { api } from '../../../lib/api';
import { formatDateTime } from '../../../lib/format';
import { t } from '../../../lib/i18n';
import { useLiveChannel } from '../../../lib/realtime';
import { useCan } from '../../../lib/session';
import { useSite } from '../../../lib/site';
import { Alert, Badge, Button, ConfirmButton, LinkButton, PageHeader, Spinner, TableWrap, TextField, Th } from '../../../components/ui';
import { ErrorLine, ErrorPanel } from '../../../components/ErrorPanel';
import { LocationScatter } from '../../../components/phase3';
import { boardKey, useBoard } from './api';

const CHANNEL = 'lone-working';
/** The live channel is the main route; this slow poll is the safety net if it silently drops (it is a safety board). */
const POLL_LIVE_MS = 30_000;
const POLL_OFFLINE_MS = 5_000;

const STATE_TONE: Record<LoneWorkSession['state'], 'neutral' | 'good' | 'warn' | 'bad'> = {
  ok: 'good',
  prompted: 'warn',
  second_prompt: 'warn',
  escalated: 'bad',
  panic: 'bad',
  ended: 'neutral',
};

/** Joins one site's board room; renders nothing. */
function LiveSite({ siteId, onRefresh, onConnected }: { siteId: string; onRefresh: () => void; onConnected: (c: boolean) => void }) {
  const { connected } = useLiveChannel(CHANNEL, siteId, onRefresh, onRefresh);
  useEffect(() => onConnected(connected), [connected, onConnected]);
  return null;
}

/** Live lone-working board (lone-working.md s6): escalations first, then who is being watched. */
export function LoneBoardPage() {
  const qc = useQueryClient();
  const { sites } = useSite();
  const canRespond = useCan('lone_working.respond');
  const canReport = useCan('lone_working.view');
  const [connected, setConnected] = useState(false);
  const q = useBoard(connected ? POLL_LIVE_MS : POLL_OFFLINE_MS);
  const refresh = () => void qc.invalidateQueries({ queryKey: boardKey });
  const siteIds = useMemo(() => [...new Set([...sites.map((s) => s.id), ...(q.data?.sessions.map((s) => s.siteId) ?? [])])], [sites, q.data]);
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 15_000);
    return () => clearInterval(id);
  }, []);

  if (q.isError && !q.data) return <ErrorPanel error={q.error} onRetry={() => void q.refetch()} />;
  if (!q.data) return <Spinner />;
  const board = q.data;
  const open = board.escalations.filter((e) => e.resolvedAt === null && e.clearedByCheckInAt === null);
  const closed = board.escalations.filter((e) => !open.includes(e));
  const watching = board.sessions.filter((s) => s.state !== 'ended');
  const panics = open.filter((e) => e.location);

  return (
    <div className="space-y-6">
      {siteIds.map((id) => (
        <LiveSite key={id} siteId={id} onRefresh={refresh} onConnected={setConnected} />
      ))}
      <PageHeader
        title={t('p3lw.board.title')}
        description={t('p3lw.board.description')}
        actions={canReport ? <LinkButton to="/lone-working/report">{t('p3lw.board.report')}</LinkButton> : undefined}
      />
      {board.monitoringDelayed ? (
        <div role="alert" className="rounded-md border-2 border-red-700 bg-red-50 p-4 text-red-900">
          <p className="font-semibold">{t('p3lw.board.delayed.title')}</p>
          <p className="mt-1 text-sm">{t('p3lw.board.delayed.body')}</p>
          {board.monitoringDelayedReason === 'no_watchdog_email' ? <p className="mt-1 text-sm font-semibold">{t('lone.monitoring.no_watchdog_email')}</p> : null}
        </div>
      ) : null}
      {q.isError ? (
        <Alert>{t('p3lw.board.stale', { at: formatDateTime(new Date(q.dataUpdatedAt).toISOString()) })}</Alert>
      ) : !connected ? (
        <Alert kind="info">{t('p3lw.board.offline')}</Alert>
      ) : (
        <p className="text-xs text-slate-600" role="status">
          {t('p3lw.board.live')}
        </p>
      )}

      <section aria-labelledby="lw-esc" className="space-y-3">
        <h2 id="lw-esc" className="text-lg font-semibold text-slate-900">
          {t('p3lw.esc.title')}
        </h2>
        {open.length === 0 ? <p className="text-sm text-slate-700">{t('p3lw.esc.none')}</p> : null}
        {open.map((e) => (
          <EscalationCard key={e.id} e={e} canRespond={canRespond} onChanged={refresh} />
        ))}
        {panics.length > 0 ? (
          <LocationScatter
            title={t('p3lw.esc.map')}
            center={{ lat: panics[0]!.location!.lat, lng: panics[0]!.location!.lng }}
            points={panics.map((e) => ({ id: e.id, name: e.memberName, lat: e.location!.lat, lng: e.location!.lng, accuracyM: e.location!.accuracyM }))}
          />
        ) : null}
      </section>

      <section aria-labelledby="lw-sessions" className="space-y-3">
        <h2 id="lw-sessions" className="text-lg font-semibold text-slate-900">
          {t('p3lw.sessions.title')}
        </h2>
        <TableWrap caption={t('p3lw.sessions.title')}>
          <thead className="border-b border-slate-200 bg-slate-50">
            <tr>
              <Th>{t('p3lw.sessions.person')}</Th>
              <Th>{t('p3lw.sessions.site')}</Th>
              <Th>{t('p3lw.sessions.state')}</Th>
              <Th>{t('p3lw.sessions.last')}</Th>
              <Th>{t('p3lw.sessions.next')}</Th>
              <Th>{t('p3lw.sessions.until')}</Th>
              <Th />
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {watching.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-4 py-6 text-center text-sm text-slate-600">
                  {t('p3lw.sessions.none')}
                </td>
              </tr>
            ) : (
              watching.map((s) => (
                <SessionRow key={s.id} s={s} now={now} canRespond={canRespond} onChanged={refresh} />
              ))
            )}
          </tbody>
        </TableWrap>
      </section>

      {closed.length > 0 ? (
        <section aria-labelledby="lw-closed" className="space-y-3">
          <h2 id="lw-closed" className="text-lg font-semibold text-slate-900">
            {t('p3lw.esc.recent')}
          </h2>
          {closed.map((e) => (
            <EscalationCard key={e.id} e={e} canRespond={false} onChanged={refresh} />
          ))}
        </section>
      ) : null}
    </div>
  );
}

function SessionRow({ s, now, canRespond, onChanged }: { s: LoneWorkSession; now: number; canRespond: boolean; onChanged: () => void }) {
  const end = useMutation({ mutationFn: () => api.post(`/lone-working/sessions/${s.id}/end`, { schema: LoneWorkSession }), onSuccess: onChanged });
  const overdue = s.nextDueAt !== null && Date.parse(s.nextDueAt) < now && s.state === 'ok';
  return (
    <tr className={s.noContacts ? 'bg-red-50' : ''}>
      <td className="px-4 py-2.5 font-medium">{s.memberName}</td>
      <td className="px-4 py-2.5">{s.siteName}</td>
      <td className="px-4 py-2.5">
        <div className="flex flex-wrap items-center gap-2">
          <Badge tone={STATE_TONE[s.state]}>{t(`p3lw.state.${s.state}`)}</Badge>
          {s.noContacts ? <Badge tone="bad">{t('p3lw.sessions.noContacts')}</Badge> : null}
        </div>
      </td>
      <td className="px-4 py-2.5">{s.lastCheckInAt ? formatDateTime(s.lastCheckInAt) : t('p3lw.sessions.never')}</td>
      <td className="px-4 py-2.5">
        {s.nextDueAt ? formatDateTime(s.nextDueAt) : '-'}
        {overdue ? <span className="ml-2 text-xs font-semibold text-red-700">{t('p3lw.sessions.overdue')}</span> : null}
      </td>
      <td className="px-4 py-2.5">{s.plannedEndAt ? formatDateTime(s.plannedEndAt) : '-'}</td>
      <td className="px-4 py-2.5 text-right">
        {canRespond ? <ConfirmButton label={t('p3lw.sessions.end')} confirmLabel={t('p3lw.sessions.endConfirm')} pending={end.isPending} onConfirm={() => end.mutate()} /> : null}
        <ErrorLine error={end.error} />
      </td>
    </tr>
  );
}

function EscalationCard({ e, canRespond, onChanged }: { e: LoneWorkEscalation; canRespond: boolean; onChanged: () => void }) {
  const [resolving, setResolving] = useState(false);
  const [text, setText] = useState('');
  const ack = useMutation({ mutationFn: () => api.post(`/lone-working/escalations/${e.id}/ack`, { schema: LoneWorkEscalation }), onSuccess: onChanged });
  const resolve = useMutation({
    mutationFn: () => api.post(`/lone-working/escalations/${e.id}/resolve`, { body: EscalationResolve.parse({ resolution: text }), schema: LoneWorkEscalation }),
    onSuccess: () => {
      setResolving(false);
      setText('');
      onChanged();
    },
  });
  const isOpen = e.resolvedAt === null && e.clearedByCheckInAt === null;
  const panic = e.reason === 'panic';
  return (
    <article aria-label={`${t(`p3lw.reason.${e.reason}`)}: ${e.memberName}`} className={`rounded-xl border-2 p-4 shadow-sm ${isOpen ? 'border-red-700 bg-red-50' : 'border-slate-200 bg-white'}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="text-base font-semibold text-slate-900">
            {e.memberName} <span className="font-normal text-slate-700">· {e.siteName}</span>
          </h3>
          <p className="text-sm text-slate-800">
            <span className={panic ? 'font-bold uppercase text-red-800' : 'font-semibold'}>{t(`p3lw.reason.${e.reason}`)}</span> · {t('p3lw.esc.started', { at: formatDateTime(e.startedAt) })}
            {e.repeats > 0 ? ` · ${t('p3lw.esc.repeats', { n: e.repeats })}` : ''}
          </p>
        </div>
        {e.acknowledgedAt ? (
          <Badge tone="good">{t('p3lw.esc.ackedBy', { who: e.acknowledgedByLabel ?? '', at: formatDateTime(e.acknowledgedAt) })}</Badge>
        ) : isOpen ? (
          <Badge tone="bad">{t('p3lw.esc.notAcked')}</Badge>
        ) : null}
      </div>

      {e.delaySeconds > 0 ? (
        <p role="alert" className="mt-3 rounded-md border border-amber-400 bg-amber-50 p-2 text-sm text-amber-900">
          {t('p3lw.esc.delay', { minutes: Math.max(1, Math.round(e.delaySeconds / 60)) })}
        </p>
      ) : null}
      {e.deliveryFailures > 0 ? (
        <p role="alert" className="mt-3 rounded-md border-2 border-red-700 bg-white p-2 text-sm font-semibold text-red-900">
          {t('p3lw.esc.failures', { n: e.deliveryFailures })}
        </p>
      ) : null}
      {e.location ? (
        <p className="mt-3 text-sm text-slate-800">
          {t('p3lw.esc.location', { lat: e.location.lat.toFixed(5), lng: e.location.lng.toFixed(5), accuracy: Math.round(e.location.accuracyM) })}{' '}
          <a className="text-[var(--brand-primary)] underline" target="_blank" rel="noopener noreferrer" href={`https://www.openstreetmap.org/?mlat=${e.location.lat}&mlon=${e.location.lng}#map=17/${e.location.lat}/${e.location.lng}`}>
            {t('p3lw.esc.openMap')}
          </a>
        </p>
      ) : null}
      {e.clearedByCheckInAt ? <p className="mt-3 text-sm text-green-900">{t('p3lw.esc.cleared', { at: formatDateTime(e.clearedByCheckInAt) })}</p> : null}
      {e.resolvedAt ? (
        <p className="mt-3 text-sm text-slate-800">
          {t('p3lw.esc.resolved', { at: formatDateTime(e.resolvedAt) })} {e.resolution ?? ''}
        </p>
      ) : null}

      {canRespond && isOpen ? (
        <div className="mt-3 space-y-3">
          <div className="flex flex-wrap gap-2">
            {!e.acknowledgedAt ? (
              <Button variant="primary" disabled={ack.isPending} onClick={() => ack.mutate()}>
                {t('p3lw.esc.ack')}
              </Button>
            ) : null}
            {!resolving ? <Button onClick={() => setResolving(true)}>{t('p3lw.esc.resolve')}</Button> : null}
          </div>
          {resolving ? (
            <div className="space-y-2">
              <TextField label={t('p3lw.esc.resolution')} hint={t('p3lw.esc.resolutionHint')} value={text} maxLength={1000} onChange={(ev) => setText(ev.target.value)} />
              <div className="flex gap-2">
                <Button onClick={() => setResolving(false)}>{t('common.cancel')}</Button>
                <Button variant="primary" disabled={text.trim() === '' || resolve.isPending} onClick={() => resolve.mutate()}>
                  {t('p3lw.esc.resolveConfirm')}
                </Button>
              </div>
            </div>
          ) : null}
          <ErrorLine error={ack.error ?? resolve.error} />
        </div>
      ) : null}
    </article>
  );
}

